import { useCallback, useState } from 'react'
import { useLocation } from 'react-router-dom'
import compact from 'lodash/compact'
import { IShipmentRecommendationsSourceEnum, Maybe } from 'types/FMSGraphqlTypes'
import { IRecommendedShipmentFragment } from 'apps/fleet-planner/components/shipment/ShipmentCard/graphql/RecommendedShipmentFragment'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { PER_PAGE } from '../constants'
import {
  IRecommendedShipmentsQuery,
  useRecommendedShipmentsQuery,
} from '../graphql/RecommendedShipments'
import { getViewTrackingEventInput, getTrackingRecSource } from '../utils'
import { useTrackIntelligentRecs } from './useTrackIntelligentRecs'

interface IUseRecommendedShipmentsState {
  recommendations: IRecommendedShipmentFragment[]
  recommendationSource: Maybe<IShipmentRecommendationsSourceEnum>
  isLoading: boolean
  isLoadingMore: boolean
  hasRecommendations: boolean
  hasNextPage: Maybe<boolean>
  loadMoreHandler: () => Promise<void>
}

export const useRecommendedShipments = (): IUseRecommendedShipmentsState => {
  const location = useLocation()
  const { trackEvent } = useFMSAnalytics()

  const [currentPage, setCurrentPage] = useState<number>(0)
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)

  const { trackIntelligentRecEvent } = useTrackIntelligentRecs()

  const callSnowflakeViewEvent = useCallback(
    (recommendations: IRecommendedShipmentFragment[], page: number) => {
      const input = getViewTrackingEventInput({
        page,
        recommendations,
      })
      trackIntelligentRecEvent(input)
    },
    [trackIntelligentRecEvent]
  )

  const trackLoadedShipments = useCallback(
    ({ data, page = 0 }: { data: IRecommendedShipmentsQuery; page?: number }) => {
      const { shipmentRecommendations } = data || {}
      const { shipments, source } = shipmentRecommendations || {}
      const { nodes, pageInfo } = shipments || {}
      const { hasNextPage } = pageInfo || {}
      const recommendations = compact<IRecommendedShipmentFragment>(nodes)

      /**
       * If we're on the first page but the amount of results is greater
       * than the per page limit, we know that we're reading from cache
       * and should not trigger events.
       */
      if (page === 0 && recommendations.length > PER_PAGE) {
        // Update the current page number to the accurate page given the cached results
        setCurrentPage(recommendations.length / PER_PAGE - 1)
        return
      }

      const hasRecommendations = recommendations && recommendations.length > 0
      const recType = getTrackingRecSource(source)

      // If past first page
      if (page > 0) {
        if (location.pathname.includes('/home')) {
          trackEvent('Homepage', 'HOME_RECS_LOADED_MORE')
        } else {
          trackEvent('Loads for You', `LOADED_MORE_${recType}_RECS`)
        }
      }

      // If there is not a next page
      if (hasRecommendations && !hasNextPage) {
        if (location.pathname.includes('/home')) {
          trackEvent('Homepage', 'HOME_RECS_REACHED_END')
        } else {
          trackEvent('Loads for You', `END_OF_${recType}_RECS`)
        }
      }

      recommendations?.forEach(rec => {
        if (rec) {
          if (!location.pathname.includes('/home')) {
            trackEvent('Loads for You', `VIEWED_${recType}_REC_CARD`, {
              shipmentId: rec.shipmentId,
            })
          }
        }
      })

      callSnowflakeViewEvent(recommendations, page)
    },
    [callSnowflakeViewEvent, location.pathname, trackEvent]
  )

  // GraphQL Query
  const { data, fetchMore, loading: isLoading } = useRecommendedShipmentsQuery({
    variables: {
      first: PER_PAGE,
    },
    onCompleted: data => {
      // onCompleted is only called after first page is loaded
      trackLoadedShipments({ data })
    },
  })
  const { shipmentRecommendations } = data || {}
  const { shipments, source } = shipmentRecommendations || {}
  const { nodes, pageInfo } = shipments || {}
  const { endCursor, hasNextPage } = pageInfo || {}
  const recommendations = compact<IRecommendedShipmentFragment>(nodes)

  // Function used to load more recommendations
  const loadMoreHandler = useCallback(async () => {
    if (fetchMore && !isLoadingMore) {
      setIsLoadingMore(true)
      const { data } = await fetchMore({
        variables: {
          first: PER_PAGE,
          after: endCursor,
        },
      })
      setCurrentPage(currentPage => currentPage + 1)
      trackLoadedShipments({ data, page: currentPage + 1 })
      setIsLoadingMore(false)
    }
  }, [fetchMore, isLoadingMore, endCursor, trackLoadedShipments, currentPage])

  return {
    recommendations,
    recommendationSource: source,
    isLoading,
    isLoadingMore,
    hasRecommendations: recommendations.length > 0,
    hasNextPage,
    loadMoreHandler,
  }
}
